import React, { useEffect, useState } from 'react';
import { submitStrategyEvaluatorReport } from '../../services/api';
import useMitiSlotMachine from '../../hooks/useMitiSlotMachine';
import ContemplatingIndicator from '../chat/ContemplatingIndicator';
import SkillUsedReasoningCard from './SkillUsedReasoningCard';
import '../dialogs/DialogBase.css';
import '../dialogs/FeedbackDialog.css';
import './ReportSpecificStrategiesPanel.css';

function PatientDescription({ value }) {
  const text = String(value || '').replace(/^<b>Patient:<\/b>\s*/i, '').trim();
  return text ? <p>{text}</p> : null;
}

function ReportSpecificStrategiesPanel({
  t,
  theme,
  strategiesFlow,
  MitiChipComponent,
  onShowAutomatedMiscMitiCounsellor,
  sidebarMode = false,
  onSidebarScrollChange,
}) {
  const [analysisVisible, setAnalysisVisible] = useState(true);
  const [feedbackReportOpen, setFeedbackReportOpen] = useState(false);
  const [feedbackReportText, setFeedbackReportText] = useState('');
  const [feedbackReportSubmitting, setFeedbackReportSubmitting] = useState(false);
  const [feedbackReportError, setFeedbackReportError] = useState('');
  const {
    exercise,
    status,
    evaluation,
    automitiLabels = [],
    classifierExplanation = '',
    loading,
    selectStrategy,
    attemptId,
    conversation = [],
    turnResults = [],
    viewedTurnIndex = null,
    interimGuidanceVisible = false,
    revealGuidanceTurn,
    hideInterimGuidance,
  } = strategiesFlow || {};
  const stimulusTurnCount = exercise?.turn_count || 0;
  const submittedTurnCount = conversation.filter(
    (turn, index) => index >= stimulusTurnCount && turn.speaker === 'counsellor',
  ).length;
  const guidanceTurnIndex = viewedTurnIndex === null
    ? Math.max(0, submittedTurnCount - 1)
    : viewedTurnIndex;
  const effectiveTurnIndex = viewedTurnIndex === null
    ? turnResults.length - 1
    : viewedTurnIndex;
  const viewedResult = effectiveTurnIndex >= 0 ? turnResults[effectiveTurnIndex] : null;
  const viewingPastTurn = viewedTurnIndex !== null && viewedTurnIndex < turnResults.length - 1;
  const displayedEvaluation = viewingPastTurn ? viewedResult?.evaluation : evaluation;
  const displayedLabels = viewingPastTurn
    ? (viewedResult?.automiti_labels || [])
    : automitiLabels;
  const displayedExplanation = viewingPastTurn
    ? (viewedResult?.classifier_explanation || '')
    : classifierExplanation;
  // AutoMITI and the simulated client are independent calls. Open analysis as
  // soon as the trainee submits so the classifier animation does not wait for
  // the client reply to arrive.
  const isAnalysisLoading = loading && !viewingPastTurn;
  const showAnalysis = status === 'complete' || Boolean(displayedEvaluation) || isAnalysisLoading;
  const slotLabel = useMitiSlotMachine(isAnalysisLoading && displayedLabels.length === 0);
  const concealInterimGuidance = (
    status === 'active' &&
    Boolean(displayedEvaluation?.feedback) &&
    !interimGuidanceVisible
  );
  const feedbackReportWordCount = feedbackReportText.trim()
    ? feedbackReportText.trim().split(/\s+/).filter(Boolean).length
    : 0;
  const canReportGuidance = Boolean(displayedEvaluation?.feedback) && (
    status === 'complete' || (status === 'active' && interimGuidanceVisible)
  );
  const feedbackType = effectiveTurnIndex < turnResults.length - 1 ? 'interim' : (status === 'active' ? 'interim' : 'final');
  const latestTraineeTurn = viewedResult || [...conversation]
    .reverse()
    .find((turn) => turn.speaker === 'counsellor' && turn.ruler_asked !== undefined);
  const hasSubmittedTurn = conversation.some(
    (turn, index) => index >= stimulusTurnCount && turn.speaker === 'counsellor',
  );

  useEffect(() => {
    if (
      status === 'active'
      && interimGuidanceVisible
      && displayedEvaluation?.feedback
    ) {
      revealGuidanceTurn?.(guidanceTurnIndex);
    }
  }, [
    displayedEvaluation?.feedback,
    guidanceTurnIndex,
    interimGuidanceVisible,
    revealGuidanceTurn,
    status,
  ]);

  const closeFeedbackReport = () => {
    setFeedbackReportOpen(false);
    setFeedbackReportText('');
    setFeedbackReportSubmitting(false);
    setFeedbackReportError('');
  };

  const handleFeedbackReportSubmit = async () => {
    if (!attemptId || !feedbackReportText.trim() || feedbackReportSubmitting) return;
    setFeedbackReportSubmitting(true);
    setFeedbackReportError('');
    try {
      await submitStrategyEvaluatorReport({
        attemptId,
        feedbackType,
        reportText: feedbackReportText.trim(),
      });
      closeFeedbackReport();
    } catch (error) {
      setFeedbackReportError(
        error?.response?.data?.error || 'Failed to submit report. Please try again.',
      );
      setFeedbackReportSubmitting(false);
    }
  };

  if (!exercise) return null;

  if (!showAnalysis) {
    return (
      <div className={`oars-context-panel ${sidebarMode ? 'oars-sidebar-context-panel' : 'oars-report-context-panel'}`}>
        <div className="analytics-header">
          <h4 className="analytics-title">Conversation Context</h4>
          <p className="analytics-subtitle">Background on the client for this exercise</p>
        </div>
        <div className="oars-context-body">
          <PatientDescription value={exercise.patient_description} />
        </div>
        <div className="oars-action-bar">
          <div className="oars-next-exercise-wrap">
            <button
              type="button"
              className="oars-btn oars-btn--primary"
              onClick={() => selectStrategy?.('Scaling Questions')}
              disabled={loading}
            >
              Next Exercise
            </button>
            {hasSubmittedTurn && (
              <div className="oars-next-exercise-tooltip" role="status">
                This exercise is still in progress. Choosing Next Exercise will skip it.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={sidebarMode ? 'specific-strategies-sidebar-feedback' : 'oars-analytics-shell'}>
      <div
        className={sidebarMode ? 'oars-sidebar-feedback-scroll' : 'in-session-analytics oars-analytics-content'}
        onScroll={sidebarMode
          ? (event) => onSidebarScrollChange?.(event.currentTarget.scrollTop > 1)
          : undefined}
      >
        {!sidebarMode && (
          <div className="analytics-header">
            <div className="analytics-header-row">
              <div className="analytics-header-copy">
                <h4 className="analytics-title">{t('report_oars_title')}</h4>
              </div>
              <div className="analytics-header-right">
                <button
                  type="button"
                  className="analytics-toggle-chevron"
                  onClick={() => setAnalysisVisible((visible) => !visible)}
                  aria-expanded={analysisVisible}
                  aria-label={analysisVisible ? t('report_hide_analytics_aria') : t('report_show_analytics_aria')}
                  title={analysisVisible ? t('report_hide_analytics_aria') : t('report_show_analytics_aria')}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    {analysisVisible ? <polyline points="6 9 12 15 18 9" /> : <polyline points="6 15 12 9 18 15" />}
                  </svg>
                </button>
              </div>
            </div>
            {analysisVisible && <div className="analytics-header-divider" aria-hidden="true" />}
          </div>
        )}

        {(sidebarMode || analysisVisible) && (
          <div className={sidebarMode ? 'specific-strategies-sidebar-feedback__body' : 'analytics-scroll-body'}>
            <div className="analytics-sections">
              <SkillUsedReasoningCard
                explanation={displayedExplanation}
                codeCount={displayedLabels.length}
                theme={theme}
              >
                <div className="analytics-card-header">
                  <h5 className="analytics-card-title">Skill Used</h5>
                  {onShowAutomatedMiscMitiCounsellor && (
                    <button
                      type="button"
                      className="analytics-toggle-icon"
                      onClick={onShowAutomatedMiscMitiCounsellor}
                      aria-label="Open counsellor coding manual"
                      title="Open counsellor coding manual"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15.5A2.5 2.5 0 0 0 17.5 16H4z" />
                        <path d="M6.5 3v13" />
                        <path d="M8.5 7h8" />
                        <path d="M8.5 10.5h8" />
                        <path d="M8.5 14h5" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="analytics-card-content">
                  {isAnalysisLoading && displayedLabels.length === 0 ? (
                    <div className="oars-attempt-entry miti-slot-machine" aria-label="Analyzing skill code">
                      <div className="oars-label-row" aria-hidden="true">
                        <MitiChipComponent key={slotLabel} label={slotLabel} expand />
                      </div>
                    </div>
                  ) : (
                    <div className="oars-attempt-entry">
                      <div className="oars-label-row">
                        {(displayedLabels.length ? displayedLabels : ['NC']).map((label) => (
                          <MitiChipComponent key={label} label={label} compactTooltip />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </SkillUsedReasoningCard>

              <div className="analytics-card strategy-guidance-card">
                <div className="analytics-card-header">
                  <h5 className="analytics-card-title">Guidance</h5>
                  <div className={`strategy-guidance-card__actions${concealInterimGuidance ? ' strategy-guidance-card__actions--concealed' : ''}`}>
                    {status === 'active' && interimGuidanceVisible && displayedEvaluation?.feedback && (
                      <button
                        type="button"
                        className="analytics-toggle-icon"
                        onClick={hideInterimGuidance}
                        aria-label="Hide guidance"
                        title="Hide guidance"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                          <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                          <circle cx="12" cy="12" r="2.5" />
                          <path d="m4 4 16 16" />
                        </svg>
                      </button>
                    )}
                    <button
                      type="button"
                      className="analytics-toggle-icon feedback-report-icon"
                      onClick={() => setFeedbackReportOpen(true)}
                      aria-label="Report incorrect feedback"
                      title={canReportGuidance ? 'Report incorrect feedback' : 'Available when guidance is ready and visible'}
                      disabled={!canReportGuidance}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M4 21V5" />
                        <path d="M4 5h10l-1.4 3L18 11H4" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div
                  className={`analytics-card-content${concealInterimGuidance ? ' strategy-guidance-card__content--concealed' : ''}`}
                  aria-hidden={concealInterimGuidance ? 'true' : undefined}
                >
                  {displayedEvaluation?.feedback
                    ? <p className={`sr-feedback-text${isAnalysisLoading ? ' specific-skill-guidance-streaming' : ''}`}>{displayedEvaluation.feedback}</p>
                    : <p className="analytics-empty">{isAnalysisLoading ? <ContemplatingIndicator /> : t('report_no_data_yet')}</p>}
                </div>
                {concealInterimGuidance && (
                  <div className="strategy-guidance-card__mask">
                    <div className="strategy-guidance-card__mask-content">
                      <strong>Guidance available</strong>
                      <span>Review it now, or continue without viewing it.</span>
                      <button
                        type="button"
                        className="strategy-guidance-card__reveal-btn"
                        onClick={() => revealGuidanceTurn?.(guidanceTurnIndex)}
                      >
                        View Guidance
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {sidebarMode && <div className="oars-sidebar-feedback-tail" aria-hidden="true" />}
            </div>
          </div>
        )}
      </div>
      {feedbackReportOpen && (
        <div className="app-confirm-overlay app-feedback-overlay" role="dialog" aria-modal="true">
          <div className="app-confirm-panel app-feedback-panel" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="app-feedback-close"
              onClick={closeFeedbackReport}
              aria-label="Close report dialog"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
            <div className="app-dialog-head">
              <h4 className="app-dialog-title">Think Our Evaluator is Incorrect?</h4>
            </div>
            <div className="app-confirm-content">
              <p>If something in this evaluation seems off, you can flag it here so we can review it more closely.</p>
            </div>
            <div className="oars-feedback-context-card">
              <div className="app-feedback-field">
                <p className="app-feedback-question-text">
                  {feedbackType === 'interim' ? 'Evaluated Response' : 'Feedback Scope'}
                </p>
                <div className="message-text message-text--user oars-report-response-box">
                  <div className="oars-report-response-scroll">
                    <p className="oars-report-text">
                      {feedbackType === 'interim'
                        ? latestTraineeTurn?.trainee_text || latestTraineeTurn?.text || '—'
                        : 'Complete Scaling Questions exercise'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="app-feedback-field">
                <p className="app-feedback-question-text">Evaluator Feedback</p>
                <div className="analytics-card oars-report-feedback-card">
                  <div className="analytics-card-content oars-report-feedback-content">
                    <p className="sr-feedback-text">{displayedEvaluation?.feedback || '—'}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="app-feedback-field">
              <div className="app-feedback-field-header">
                <label className="app-feedback-label" htmlFor="strategy-report-feedback-input">Your Feedback</label>
                <span className="app-feedback-word-count">{`${feedbackReportWordCount} / 150 words`}</span>
              </div>
              <textarea
                id="strategy-report-feedback-input"
                className="app-feedback-input"
                value={feedbackReportText}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  const nextCount = nextValue.trim()
                    ? nextValue.trim().split(/\s+/).filter(Boolean).length
                    : 0;
                  if (nextCount <= 150) setFeedbackReportText(nextValue);
                }}
                rows={4}
                placeholder="Share your thoughts here..."
              />
              {feedbackReportError && <p className="specific-strategies-error">{feedbackReportError}</p>}
            </div>
            <div className="app-dialog-actions app-feedback-actions">
              <button
                type="button"
                className="app-confirm-btn primary"
                onClick={handleFeedbackReportSubmit}
                disabled={feedbackReportSubmitting || !feedbackReportText.trim()}
              >
                {feedbackReportSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportSpecificStrategiesPanel;
